import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Users,
  Calendar,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

const Contact = () => {
  const POWER_AUTOMATE_URL =
    "https://88e5ea1ee6d3e02d8fdccaf1b47550.d7.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/344a673002c74a2fbeaa80ac4eeb7b0b/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=IUzhHpn5vXUWrGYIUGmfdML4CtVCgt8QOFT1jqtEjSM";
  const formRef = useRef<HTMLFormElement | null>(null);

  const isFormValid = () => {
    const form = formRef.current;
    if (!form) return false;

    const firstName = form.first_name?.value?.trim();
    const lastName = form.last_name?.value?.trim();
    const email = form.email?.value?.trim();
    const message = form.message?.value?.trim();

    return firstName && lastName && email && message;
  };

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formRef.current) return;

    if (!isFormValid()) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    toast.loading("Sending message...");

    try {
      const form = formRef.current;

      // Get ALL form values directly from form inputs
      const firstName = form.first_name?.value?.trim() || "";
      const lastName = form.last_name?.value?.trim() || "";
      const email = form.email?.value?.trim() || "";
      const phone = form.phone?.value?.trim() || "Not Provided";
      const company = form.company?.value?.trim() || "Not Provided";
      const message = form.message?.value?.trim() || "";

      // Full formatted email body
      const fullEmailBody = `
New Website Contact Form Submission
===================================

CONTACT DETAILS
---------------
First Name: ${firstName}
Last Name: ${lastName}
Full Name: ${firstName} ${lastName}
Email Address: ${email}
Phone Number: ${phone}
Company Name: ${company}

PROJECT DETAILS
---------------
${message}

===================================
Submitted On: ${new Date().toLocaleString()}
Source: Smartborn Website Contact Form
    `.trim();

      const payload = {
        subject: `New Contact Request | ${firstName} ${lastName} | ${company}`,

        message: `
    <div style="font-family: Arial, sans-serif; line-height: 1.6;">
      <h2>New Website Contact Form Submission</h2>

      <h3>Contact Details</h3>
      <p>
        <strong>First Name:</strong> ${firstName}<br>
        <strong>Last Name:</strong> ${lastName}<br>
        <strong>Full Name:</strong> ${firstName} ${lastName}<br>
        <strong>Email Address:</strong> ${email}<br>
        <strong>Phone Number:</strong> ${phone}<br>
        <strong>Company Name:</strong> ${company}
      </p>

      <h3>Project Details</h3>
      <p>${message.replace(/\n/g, "<br>")}</p>

      <hr>

      <p>
        <strong>Submitted On:</strong> ${new Date().toLocaleString()}<br>
        <strong>Source:</strong> Smartborn Website Contact Form
      </p>
    </div>
  `.trim(),

        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`,
        email,
        phone,
        company,
        submittedAt: new Date().toISOString(),
        source: "Smartborn Website Contact Form",
      };

      const response = await fetch(POWER_AUTOMATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      toast.dismiss();
      toast.success(
        "Message sent successfully! We'll reach out to you shortly, Thank You",
      );

      form.reset();

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to send message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      details: "contact@smartborn.io",
      description: "Get a response within 24 hours",
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+91 98869 28201",
      // description: "Mon-Fri 9AM-7PM IST"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details:
        "PID No-14, new, 49, 2nd floor ,VLVC Plaza, 39-49, 19th Main Rd, 1st Block, Rajajinagar, Bengaluru, Karnataka 560010",
      // description: "Schedule an appointment"
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Monday - Friday: 9AM-7PM IST",
      description: "Weekend support available",
    },
  ];

  const offices = [
    {
      city: "Rajajinagar, Bangalore",
      address:
        "PID No-14, new 49, 2nd floor, VLVC Plaza, \n 39-49, 19th Main Rd, 1st Block, Rajajinagar, \n Bengaluru, Karnataka 560010",
      phone: "+91 98869 28201",
      email: "contact@smartborn.io",
    },
    {
      city: "MG Road, Bangalore",
      address:
        "205, Brigade Gardens, \n 19, Church St, Shanthala Nagar, Ashok Nagar, \n Bengaluru, Karnataka 560001",
      phone: "+91 98869 28201",
      email: "contact@smartborn.io",
    },
    {
      city: "Australia, Melbourne",
      address: "1 Koala Close, \n Throsby ACT 2914, \n Australia",
      phone: "+91 82203 45083",
      email: "contact@smartborn.io",
    },
    // {
    //   city: "Bangalore",
    //   address: "321 Tech Park, Bangalore, India 560001",
    //   phone: "+91 80 1234 5678",
    //   email: "bangalore@smartborn.com"
    // }
  ];

  return (
    <section
      id="contact"
      className="py-12 md:py-16 lg:py-20 bg-gradient-to-b from-background via-primary/5 to-secondary/5"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-duo-gradient bg-clip-text text-transparent pb-2">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg md:text-xl text-foreground max-w-3xl mx-auto mb-3 px-4">
            <strong>Start your project today</strong> and join 180+ companies
            achieving measurable growth
          </p>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            Get a free consultation and detailed project roadmap within 24 hours
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          {/* Contact Form */}
          <Card className="bg-gradient-card border-secondary/20 shadow-card hover:shadow-elegant transition-all duration-300">
            <CardHeader className="pb-6 border-b border-secondary/10">
              <CardTitle className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-3">
                <MessageSquare className="h-6 w-6 text-secondary" />
                Send us a Message
              </CardTitle>
            </CardHeader>
            <form ref={formRef} onSubmit={sendEmail}>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      First Name *
                    </label>
                    <Input
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) =>
                        setFormData({ ...formData, first_name: e.target.value })
                      }
                      placeholder="John"
                      className="bg-background"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Last Name *
                    </label>
                    <Input
                      name="last_name"
                      value={formData.last_name}
                      onChange={(e) =>
                        setFormData({ ...formData, last_name: e.target.value })
                      }
                      placeholder="Doe"
                      className="bg-background"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Email Address *
                  </label>
                  <Input
                    name="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    type="email"
                    placeholder="john.doe@company.com"
                    className="bg-background"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Phone Number
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="+91 98869 28201"
                    className="bg-background"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Company
                  </label>
                  <Input
                    name="company"
                    placeholder="Your Company"
                    className="bg-background"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Project Details *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder="Tell us about your project requirements, timeline, and budget..."
                    className="bg-background min-h-[120px]"
                  />
                </div>
                <Button
                  disabled={loading || !isFormValid()}
                  type="submit"
                  variant="cta"
                  size="lg"
                  className="w-full"
                >
                  {loading ? "Sending..." : "Get Free Consultation"}
                  <Send className="ml-2 h-5 w-5" />
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  📞 Expect a response within 24 hours · No commitment required
                </p>
              </CardContent>
            </form>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="grid grid-cols-1 gap-6">
              {contactInfo.map((info, index) => (
                <Card
                  key={index}
                  className={`bg-gradient-card border ${index % 2 === 0 ? "border-primary/20" : "border-secondary/20"} shadow-card hover:shadow-elegant transition-all duration-300`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`${index % 2 === 0 ? "bg-primary/10 shadow-glow border border-primary/20" : "bg-secondary/10 shadow-glow border border-secondary/20"} p-3 rounded-lg`}
                      >
                        <info.icon
                          className={`h-6 w-6 ${index % 2 === 0 ? "text-primary" : "text-secondary"}`}
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-foreground mb-1">
                          {info.title}
                        </h4>
                        <p className="text-foreground font-medium mb-1">
                          {info.details}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {info.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Global Offices */}
        <div>
          <h3 className="text-3xl font-bold text-center mb-12 text-foreground">
            <span className="bg-duo-gradient bg-clip-text text-transparent">
              Our Offices
            </span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offices.map((office, index) => (
              <Card
                key={index}
                className={`bg-gradient-card border ${index % 2 === 0 ? "border-primary/20" : "border-secondary/20"} shadow-card hover:shadow-elegant transition-all duration-300`}
              >
                <CardContent className="p-6 text-center">
                  <h4 className="text-lg font-semibold text-foreground mb-4">
                    {office.city}
                  </h4>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <MapPin
                        className={`h-4 w-4 mt-0.5 flex-shrink-0 ${index % 2 === 0 ? "text-primary" : "text-secondary"}`}
                      />
                      <span className="whitespace-pre-line text-left block">
                        {office.address}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone
                        className={`h-4 w-4 ${index % 2 === 0 ? "text-primary" : "text-secondary"}`}
                      />
                      <span>{office.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail
                        className={`h-4 w-4 ${index % 2 === 0 ? "text-primary" : "text-secondary"}`}
                      />
                      <span>{office.email}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
